import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Buildings, Plus, Trash, MapPin, Phone, Envelope, Check, PencilSimple } from "@phosphor-icons/react";

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "" });

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (error) {
      console.error("Failed to load locations:", error);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ name: "", address: "", phone: "", email: "" });
    setShowDialog(true);
  };

  const openEdit = (loc) => {
    setEditingId(loc.id);
    setForm({ name: loc.name || "", address: loc.address || "", phone: loc.phone || "", email: loc.email || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.address || !form.phone) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/locations/${editingId}`, form);
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/locations`, form);
        toast.success("Location added!");
      }
      setShowDialog(false);
      setEditingId(null);
      setForm({ name: "", address: "", phone: "", email: "" });
      fetchLocations();
    } catch (error) {
      toast.error(editingId ? "Failed to update location" : "Failed to add location");
    }
  };

  const handleToggle = async (locId, currentActive) => {
    try {
      await axios.put(`${API}/locations/${locId}`, { is_active: !currentActive });
      toast.success(currentActive ? "Location deactivated" : "Location activated");
      fetchLocations();
    } catch (error) {
      toast.error("Failed to update location");
    }
  };

  const handleDelete = async (locId) => {
    try {
      await axios.delete(`${API}/locations/${locId}`);
      toast.success("Location deleted");
      fetchLocations();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="locations-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Locations</h1>
            <p className="text-muted-foreground">Manage restaurant locations</p>
          </div>
          <Button className="gap-2" onClick={openAdd} data-testid="add-location-btn">
            <Plus size={18} /> Add Location
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>
        ) : locations.length === 0 ? (
          <Card>
            <CardContent className="text-center py-16 text-muted-foreground">
              <Buildings size={48} className="mx-auto mb-2 opacity-30" />
              <p>No locations configured</p>
              <p className="text-sm">Add your first restaurant location</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map(loc => (
              <Card key={loc.id} className={`transition-opacity ${!loc.is_active ? "opacity-50" : ""}`} data-testid={`location-${loc.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg">{loc.name}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={loc.is_active ? "default" : "secondary"} className={loc.is_active ? "bg-green-100 text-green-700 border-green-200" : ""}>
                        {loc.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin size={16} /><span>{loc.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone size={16} /><span>{loc.phone}</span>
                    </div>
                    {loc.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Envelope size={16} /><span>{loc.email}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${loc.id}`} className="text-sm text-muted-foreground">Active</Label>
                      <Switch id={`active-${loc.id}`} checked={loc.is_active !== false} onCheckedChange={() => handleToggle(loc.id, loc.is_active !== false)} />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(loc)} data-testid={`edit-loc-${loc.id}`} title="Edit location">
                        <PencilSimple size={18} />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(loc.id)} data-testid={`delete-loc-${loc.id}`} title="Delete location">
                        <Trash size={18} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showDialog} onOpenChange={(o) => { if (!o) { setShowDialog(false); setEditingId(null); } else { setShowDialog(true); } }}>
        <DialogContent data-testid="add-location-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingId ? "Edit Location" : "Add Location"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name *</Label><Input placeholder="Downtown Branch" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="loc-name" /></div>
            <div className="space-y-2"><Label>Address *</Label><Input placeholder="123 Main Street" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} data-testid="loc-address" /></div>
            <div className="space-y-2"><Label>Phone *</Label><Input placeholder="(555) 123-4567" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="loc-phone" /></div>
            <div className="space-y-2"><Label>Email</Label><Input placeholder="branch@restaurant.com" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="loc-email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-location-btn">{editingId ? "Save Changes" : "Add Location"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsPage;
